// src/components/intake/IntakeMoveDate.js
//
// Ticket 061 (plan D-OD5, post-testing round 2). The unskippable move-date
// screen: TWO mutually exclusive checkboxes. C is the oldest tax year the intake
// offers (intakeConfig moveCutoffYear).
//   "Lived here since before {C}" -> no date is asked; the shared placeholder
//                                    date is stored.
//   "Moved here {C} or after"     -> reveals month and year on ONE line, plus
//                                    the day ONLY when the month is July (the
//                                    183-day residency rule; required then and
//                                    checked against the month and year). Every
//                                    other month stores the 1st as a dummy day.
//                                    Year is four digits, from {C} to {C}+10
//                                    (a planned move is fine). The year and day
//                                    boxes accept digits only.
// Neither ticked = unanswered (Next stays disabled). No Skip, no "Not sure", no
// residency question. The stored placeholder date is never shown here.

import React from 'react'
import {
  MONTH_NAMES, moveBeforeLabel, moveRecentLabel, moveCutoffYear, moveMaxYear,
} from '../../lib/intake/intakeConfig'
import { isValidMoveDay, moveNeedsDay } from '../../lib/intake/section1Facts'

export default function IntakeMoveDate({ move, onMove, today }) {
  const before = move.branch === 'before'
  const recent = move.branch === 'recent'
  const cutoff = moveCutoffYear(today)
  const maxYear = moveMaxYear(today)
  const showDay = moveNeedsDay(move.month)
  const yearBad = move.year.length === 4 && (Number(move.year) < cutoff || Number(move.year) > maxYear)
  const dayEntered = showDay && move.day !== '' && /^\d{4}$/.test(String(move.year))
  const dayBad = dayEntered && !isValidMoveDay(move.year, move.month, move.day)
  return (
    <div className="u-stack-sm">
      <label className="cal-checkbox-label intake-checkbox-label">
        <input
          type="checkbox"
          checked={before}
          onChange={(e) => onMove({ branch: e.target.checked ? 'before' : null })}
        />
        {moveBeforeLabel(today)}
      </label>

      <label className="cal-checkbox-label intake-checkbox-label">
        <input
          type="checkbox"
          checked={recent}
          onChange={(e) => onMove({ branch: e.target.checked ? 'recent' : null })}
        />
        {moveRecentLabel(today)}
      </label>

      {recent && (
        <div className="triage-subquestion">
          <div className="intake-date-row">
            <div className="intake-date-field intake-date-field--month">
              <label className="triage-date-label" htmlFor="intake-move-month">Month</label>
              <select
                id="intake-move-month"
                className="triage-date-select intake-date-control"
                value={move.month}
                onChange={(e) => onMove({ branch: 'recent', month: e.target.value })}
              >
                <option value="">Month</option>
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={String(i + 1)}>{m}</option>
                ))}
              </select>
            </div>
            <div className="intake-date-field intake-date-field--year">
              <label className="triage-date-label" htmlFor="intake-move-year">Year</label>
              <input
                id="intake-move-year"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoComplete="off"
                placeholder={String(cutoff)}
                value={move.year}
                onChange={(e) => onMove({ branch: 'recent', year: e.target.value })}
                className="triage-date-input intake-date-control"
                aria-invalid={yearBad ? 'true' : undefined}
              />
            </div>
            {showDay && (
            <div className="intake-date-field intake-date-field--day">
              <label className="triage-date-label" htmlFor="intake-move-day">Day</label>
              <input
                id="intake-move-day"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                autoComplete="off"
                placeholder="15"
                value={move.day}
                onChange={(e) => onMove({ branch: 'recent', day: e.target.value })}
                className="triage-date-input intake-date-control"
                aria-invalid={dayBad ? 'true' : undefined}
              />
            </div>
            )}
          </div>
          {yearBad && (
            <p className="triage-date-hint" role="alert">Enter a year from {cutoff} to {maxYear}.</p>
          )}
          {dayBad && (
            <p className="triage-date-hint" role="alert">That day does not exist in the month you chose.</p>
          )}
          <p className="triage-date-hint">
            The earliest date we ask about is January {cutoff}. If you plan to move, enter the date you expect.
          </p>
        </div>
      )}
    </div>
  )
}
